import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import Pagination from '@/components/base/Pagination';
import ConfirmModal from '@/components/base/ConfirmModal';

interface Customer {
  id: number;
  name: string;
  phone: string;
  wechat: string;
  email: string;
  service: string;
  message: string;
  budget: string;
  intent: string;
  status: string;
  admin_notes: string;
  source: string;
  created_at: string;
  updated_at: string;
}

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  new: { label: '未联系', cls: 'bg-amber-50 text-amber-600 border border-amber-200' },
  following: { label: '跟进中', cls: 'bg-blue-50 text-blue-600 border border-blue-200' },
  signed: { label: '已签单', cls: 'bg-green-50 text-green-600 border border-green-200' },
  invalid: { label: '已失效', cls: 'bg-gray-100 text-gray-400 border border-gray-200' },
};

function CustomerDetailModal({ customer, onClose, onUpdate }: { customer: Customer; onClose: () => void; onUpdate: () => void }) {
  const [status, setStatus] = useState(customer.status);
  const [notes, setNotes] = useState(customer.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await supabase
      .from('customers')
      .update({ status, admin_notes: notes, updated_at: new Date().toISOString() })
      .eq('id', customer.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onUpdate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-[#1a3a5c]">客户详情</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-400 text-xs mb-1">姓名</div>
              <div className="font-medium text-[#1a3a5c]">{customer.name}</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">电话</div>
              <a href={`tel:${customer.phone}`} className="font-medium text-[#1a3a5c] hover:text-[#c9a96e] cursor-pointer">{customer.phone}</a>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">微信号</div>
              <div className="text-[#1a3a5c]">{customer.wechat || '未填写'}</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">邮箱</div>
              <div className="text-[#1a3a5c]">{customer.email || '未填写'}</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">服务意向</div>
              <div className="text-[#1a3a5c]">{customer.service || '未填写'}</div>
            </div>

            <div>
              <div className="text-gray-400 text-xs mb-1">预算</div>
              <div className="text-[#1a3a5c]">{customer.budget || '未填写'}</div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">提交时间</div>
              <div className="text-[#1a3a5c]">{new Date(customer.created_at).toLocaleString('zh-CN')}</div>
            </div>
          </div>
          {customer.message && (
            <div>
              <div className="text-gray-400 text-xs mb-1">留言内容</div>
              <div className="bg-[#f8f5f0] rounded-xl p-3 text-sm text-gray-600 leading-relaxed">{customer.message}</div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">客户状态</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_MAP).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                    status === key ? val.cls + ' ring-2 ring-offset-1 ring-[#c9a96e]' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">跟进备注</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="记录跟进情况、备注信息..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e] resize-none"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#1a3a5c] hover:bg-[#0d2137] text-white font-semibold py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? '保存中...' : saved ? '✓ 已保存' : '保存更新'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Customer | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Batch
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; danger?: boolean; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    let query = supabase.from('customers').select('*').order('created_at', { ascending: false });
    if (filterStatus) query = query.eq('status', filterStatus);
    if (filterService) query = query.eq('service', filterService);
    const { data } = await query;
    let list = data ?? [];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((c) => c.name?.toLowerCase().includes(s) || c.phone?.includes(s) || c.wechat?.toLowerCase().includes(s));
    }
    setCustomers(list);
    setSelectedIds([]);
    setCurrentPage(1);
    setLoading(false);
  }, [filterStatus, filterService, search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const totalPages = Math.max(1, Math.ceil(customers.length / pageSize));
  const paginated = customers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const allPageSelected = paginated.length > 0 && paginated.every((c) => selectedIds.includes(c.id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pageIds = paginated.map((c) => c.id);
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(merged);
    }
  };

  const handleBatchUpdateStatus = async (newStatus: string) => {
    if (!selectedIds.length) return;
    const { error } = await supabase.from('customers').update({ status: newStatus, updated_at: new Date().toISOString() }).in('id', selectedIds);
    if (error) {
      setToast({ msg: '批量更新失败：' + error.message, type: 'error' });
    } else {
      setToast({ msg: `成功更新 ${selectedIds.length} 条客户状态`, type: 'success' });
      setSelectedIds([]);
      fetchCustomers();
    }
  };

  const handleDelete = async (id: number) => {
    setConfirmModal({
      open: true,
      title: '删除客户记录',
      message: '确认删除该客户记录？此操作不可恢复。',
      danger: true,
      onConfirm: async () => {
        const { error } = await supabase.from('customers').delete().eq('id', id);
        if (error) {
          setToast({ msg: '删除失败：' + error.message, type: 'error' });
        } else {
          setToast({ msg: '已删除客户记录', type: 'success' });
          fetchCustomers();
        }
        setConfirmModal((p) => ({ ...p, open: false }));
      },
    });
  };

  const services = ['代孕服务', '试管婴儿', '捐精捐卵', '综合咨询'];

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">客户管理</h1>
          <p className="text-gray-400 text-sm mt-1">管理前端表单提交的客户线索</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <i className="ri-user-heart-line text-[#c9a96e]"></i>
          共 {customers.length} 条记录
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-[#f8f5f0] min-w-[200px]">
          <i className="ri-search-line text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索姓名、电话、微信..."
            className="bg-transparent text-sm text-gray-600 outline-none flex-1"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 cursor-pointer focus:outline-none focus:border-[#c9a96e] bg-white"
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={filterService}
          onChange={(e) => setFilterService(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 cursor-pointer focus:outline-none focus:border-[#c9a96e] bg-white"
        >
          <option value="">全部服务</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterStatus || filterService || search) && (
          <button
            onClick={() => { setFilterStatus(''); setFilterService(''); setSearch(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
          >
            <i className="ri-close-line"></i>清除筛选
          </button>
        )}
      </div>

      {/* Batch action bar */}
      {selectedIds.length > 0 && (
        <div className="bg-[#1a3a5c] rounded-2xl px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-white text-sm font-medium">已选中 {selectedIds.length} 项</span>
          <div className="flex items-center gap-2 ml-auto">
            {Object.entries(STATUS_MAP).map(([key, val]) => (
              <button
                key={key}
                onClick={() => handleBatchUpdateStatus(key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${val.cls}`}
              >
                设为{val.label}
              </button>
            ))}
            <button onClick={() => setSelectedIds([])} className="text-white/60 hover:text-white text-xs cursor-pointer ml-2">
              清除选择
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            <i className="ri-loader-4-line text-2xl animate-spin block mb-2"></i>加载中...
          </div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <i className="ri-user-search-line text-4xl mb-3 block"></i>
            <p className="text-sm">暂无客户记录</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f8f5f0]">
                <tr className="text-gray-500 text-xs">
                  <th className="px-5 py-3.5 w-10">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-5 py-3.5 font-medium">姓名</th>
                  <th className="text-left px-5 py-3.5 font-medium">联系方式</th>
                  <th className="text-left px-5 py-3.5 font-medium">服务意向</th>
                  <th className="text-left px-5 py-3.5 font-medium">状态</th>
                  <th className="text-left px-5 py-3.5 font-medium">提交时间</th>
                  <th className="text-left px-5 py-3.5 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.map((c) => {
                  const s = STATUS_MAP[c.status] ?? STATUS_MAP.new;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="w-4 h-4 rounded border-gray-300 text-[#c9a96e] focus:ring-[#c9a96e] cursor-pointer"
                        />
                      </td>
                      <td className="px-5 py-4 font-medium text-[#1a3a5c]">
                        {c.name}
                        {c.admin_notes && (
                          <i className="ri-sticky-note-line text-[#c9a96e] text-xs ml-1.5" title={c.admin_notes}></i>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-500">
                        <div>{c.phone}</div>
                        {c.wechat && <div className="text-xs text-gray-400">微信: {c.wechat}</div>}
                      </td>
                      <td className="px-5 py-4 text-gray-500">{c.service || '未填写'}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                      </td>
                      <td className="px-5 py-4 text-gray-400 text-xs">
                        {new Date(c.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setSelected(c)}
                            className="text-xs text-[#c9a96e] hover:underline cursor-pointer"
                          >
                            查看详情
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="text-xs text-gray-400 hover:text-red-500 cursor-pointer"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={customers.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {selected && (
        <CustomerDetailModal
          customer={selected}
          onClose={() => setSelected(null)}
          onUpdate={() => { fetchCustomers(); setSelected(null); }}
        />
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        danger={confirmModal.danger}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((p) => ({ ...p, open: false }))}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-[60] px-5 py-3 rounded-xl shadow-lg text-sm font-medium transition-all">
          <div className={`${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'} px-4 py-3 rounded-xl flex items-center gap-2`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-error-warning-fill text-red-500'}`}></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}